/**
 * Simplified API Hook System
 * 
 * Replaces complex TanStack Query with simple hooks that handle loading states.
 * Uses direct API calls with built-in error handling and caching.
 */

import { useState, useEffect, useCallback } from 'react'
import type { ConversationFilters, CustomerFilters, User } from '@/lib/types'
import * as api from '@/lib/api'
import { handleApiError } from '@/lib/error-utils'
import { toast } from 'sonner'

// === GENERIC HOOK ===

type ApiState<T> = {
  data: T | null
  loading: boolean
  error: string | null
}

function useApiCall<T>(
  apiCall: () => Promise<T>,
  dependencies: any[] = []
): ApiState<T> & { refetch: () => Promise<void> } {
  const [state, setState] = useState<ApiState<T>>({
    data: null,
    loading: true,
    error: null,
  })

  const fetchData = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }))
      const data = await apiCall()
      setState({ data, loading: false, error: null })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred'
      setState({ 
        data: null, 
        loading: false, 
        error: errorMessage
      })
      // Show toast for all API errors automatically
      handleApiError(error)
    }
  }, dependencies)

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return {
    ...state,
    refetch: fetchData,
  }
}

// === AUTH HOOKS ===

export function useCurrentUser() {
  const [state, setState] = useState<ApiState<User>>({
    data: null,
    loading: true,
    error: null,
  })

  const fetchData = useCallback(async () => {
    // Check if we have a token before making the API call
    const token = api.getAuthToken()
    if (!token) {
      setState({ data: null, loading: false, error: null })
      return
    }

    try {
      setState(prev => ({ ...prev, loading: true, error: null }))
      const data = await api.getCurrentUser()
      
      // Update stored companies and current company from the response
      if (data.companies) {
        localStorage.setItem('user_companies', JSON.stringify(data.companies))
      }
      if (data.current_company) {
        localStorage.setItem('current_company', JSON.stringify(data.current_company))
        // Set the company ID in the API client so subsequent API calls include the X-Company-ID header
        api.setCompanyId(data.current_company.company_id)
      }
      
      setState({ data, loading: false, error: null })
    } catch (error: any) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred'
      const errorStatus = error?.status || 0
      
      setState({ 
        data: null, 
        loading: false, 
        error: errorStatus === 401 ? `401:${errorMessage}` : errorMessage
      })
      
      // Only show toast for auth errors if we're not on the login page
      const isLoginPage = typeof window !== 'undefined' && window.location.pathname === '/login'
      if (!isLoginPage && errorStatus !== 401) {
        handleApiError(error)
      }
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return {
    ...state,
    refetch: fetchData,
  }
}

// === USER MANAGEMENT ===

export function useUsers(params: {
  app_id?: string;
  role?: string;
  status?: string;
} = {}) {
  return useApiCall(() => api.getUsers(params), [
    params.app_id,
    params.role,
    params.status,
  ])
}

export function useLogin() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const login = async (email: string, password: string) => {
    try {
      setLoading(true)
      setError(null)
      
      // Check if user has a previously selected company to restore
      const lastSelectedCompanyId = localStorage.getItem('last_selected_company_id')
      
      const result = await api.login({ 
        email, 
        password,
        ...(lastSelectedCompanyId ? { company_id: lastSelectedCompanyId } : {})
      })
      
      // Store user and company data
      localStorage.setItem('user_data', JSON.stringify(result.user))
      if (result.current_company) {
        localStorage.setItem('current_company', JSON.stringify(result.current_company))
        // Clear the last selected company ID since we've successfully restored it
        localStorage.removeItem('last_selected_company_id')
      }
      if (result.companies) {
        localStorage.setItem('user_companies', JSON.stringify(result.companies))
      }
      
      return result
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed'
      setError(errorMessage)
      // Show toast for login errors
      handleApiError(error, 'Login failed. Please check your credentials.')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const logout = async () => {
    try {
      await api.logout()
      
      // Clear stored data
      localStorage.removeItem('user_data')
      localStorage.removeItem('current_company')
      localStorage.removeItem('user_companies')
      localStorage.removeItem('auth_token')
      localStorage.removeItem('company_id')
      
      // Redirect to login
      window.location.href = '/login'
    } catch (error) {
      console.error('Logout error:', error)
      // Still clear auth-related data even if API call fails
      localStorage.removeItem('user_data')
      localStorage.removeItem('current_company')
      localStorage.removeItem('user_companies')
      localStorage.removeItem('auth_token')
      localStorage.removeItem('company_id')
      window.location.href = '/login'
    }
  }

  return { login, logout, loading, error }
}

export function usePasswordReset() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const requestReset = async (email: string) => {
    try {
      setLoading(true)
      setError(null)
      const result = await api.requestPasswordReset(email)
      return result
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Password reset request failed'
      setError(errorMessage)
      handleApiError(error, 'Failed to request password reset. Please try again.')
      throw error
    } finally {
      setLoading(false)
    }
  }

  return { requestReset, loading, error }
}

export function useResetPasswordWithHash() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const resetPassword = async (hash: string, password: string) => {
    try {
      setLoading(true)
      setError(null)
      const result = await api.resetPasswordWithHash(hash, password)
      if (result.code === "success") {
        toast.success(result.message || "Password reset successful")
      }
      return result
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Password reset failed'
      setError(errorMessage)
      handleApiError(error, 'Failed to reset password. Please try again.')
      throw error
    } finally {
      setLoading(false)
    }
  }

  return { resetPassword, loading, error }
}

// === APPS HOOKS ===

export function useApps(params: { page?: number; limit?: number } = {}) {
  return useApiCall(() => api.getApps(params), [params.page, params.limit])
}

export function useApp(appId: string | null) {
  return useApiCall(
    () => appId ? api.getApp(appId) : Promise.reject(new Error('No app ID')),
    [appId]
  )
}

// === CONVERSATIONS HOOKS ===

export function useDashboardConversations(params: { app_id?: string; limit?: number } = {}) {
  return useApiCall(() => api.getDashboardConversations(params), [params.app_id, params.limit])
}

export function useConversations(filters: ConversationFilters = {}) {
  return useApiCall(() => api.getConversations(filters), [
    filters.app_id,
    filters.page,
    filters.limit,
    filters.status,
    filters.channel,
    filters.assigned_user_id,
    filters.queue_id,
    filters.search,
  ])
}


// === CUSTOMERS HOOKS ===

export function useCustomers(filters: CustomerFilters = {}) {
  return useApiCall(() => api.getCustomers(filters), [
    filters.page,
    filters.limit,
    filters.search,
    filters.has_conversations,
  ])
}

export function useCustomer(customerId: string | null) {
  return useApiCall(
    () => customerId ? api.getCustomer(customerId) : Promise.reject(new Error('No customer ID')),
    [customerId]
  )
}

// === QUEUES HOOKS ===

export function useQueues(appId?: string, filters?: { is_active?: boolean }) {
  return useApiCall(() => api.getQueues({ 
    ...(appId ? { app_id: appId } : {}),
    ...filters 
  }), [appId, filters?.is_active])
}

export function useQueueUsers(queueId: string | undefined) {
  return useApiCall(
    () => queueId ? api.getQueueUsers(queueId) : Promise.resolve([]), 
    [queueId]
  )
}

// === CONVERSATION ACTIONS ===

export function useFlagConversation() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const flag = async (conversationId: string, flagged: boolean) => {
    try {
      setLoading(true)
      setError(null)
      await api.flagConversation(conversationId, flagged)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to flag conversation'
      setError(errorMessage)
      handleApiError(error, 'Failed to update flag status')
      throw error
    } finally {
      setLoading(false)
    }
  }

  return { flag, loading, error }
}

export function useMarkAsRead() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const markAsRead = async (conversationId: string) => {
    try {
      setLoading(true)
      setError(null)
      await api.markConversationAsRead(conversationId)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to mark as read'
      setError(errorMessage)
      handleApiError(error, 'Failed to mark conversation as read')
      throw error
    } finally {
      setLoading(false)
    }
  }

  return { markAsRead, loading, error }
}

export function useSendMessage() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const sendMessage = async (conversationId: string, content: string) => {
    try {
      setLoading(true)
      setError(null)
      const result = await api.sendMessage(conversationId, content)
      return result
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send message'
      setError(errorMessage)
      handleApiError(error, 'Failed to send message')
      throw error
    } finally {
      setLoading(false)
    }
  }

  return { sendMessage, loading, error }
}

export function useAssignConversation() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const assign = async (conversationId: string, userId: string) => {
    try {
      setLoading(true)
      setError(null)
      await api.assignConversation(conversationId, userId)
      toast.success('Conversation assigned successfully')
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to assign conversation'
      setError(errorMessage)
      handleApiError(error, 'Failed to assign conversation')
      throw error
    } finally {
      setLoading(false)
    }
  }

  return { assign, loading, error }
}

export function useUnassignConversation() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const unassign = async (conversationId: string) => {
    try {
      setLoading(true)
      setError(null)
      await api.unassignConversation(conversationId)
      toast.success('Conversation unassigned')
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to unassign conversation'
      setError(errorMessage)
      handleApiError(error, 'Failed to unassign conversation')
      throw error
    } finally {
      setLoading(false)
    }
  }

  return { unassign, loading, error }
}

export function useTransferConversation() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const transfer = async (
    conversationId: string, 
    queueId: string, 
    userId?: string,
    transferReason?: string
  ) => {
    try {
      setLoading(true)
      setError(null)
      await api.transferConversation(conversationId, queueId, userId, transferReason)
      toast.success('Conversation transferred successfully')
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to transfer conversation'
      setError(errorMessage)
      handleApiError(error, 'Failed to transfer conversation')
      throw error
    } finally {
      setLoading(false)
    }
  }

  return { transfer, loading, error }
}

export function useResolveConversation() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const resolve = async (conversationId: string) => {
    try {
      setLoading(true)
      setError(null)
      await api.resolveConversation(conversationId)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to resolve conversation'
      setError(errorMessage)
      handleApiError(error, 'Failed to resolve conversation')
      throw error
    } finally {
      setLoading(false)
    }
  }

  return { resolve, loading, error }
}

export function useDeleteConversation() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const deleteConversation = async (conversationId: string) => {
    try {
      setLoading(true)
      setError(null)
      await api.deleteConversation(conversationId)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete conversation'
      setError(errorMessage)
      handleApiError(error, 'Failed to delete conversation')
      throw error
    } finally {
      setLoading(false)
    }
  }

  return { deleteConversation, loading, error }
}

export function useGenerateSummary() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const generateSummary = async (conversationId: string) => {
    try {
      setLoading(true)
      setError(null)
      const result = await api.generateConversationSummary(conversationId)
      return result.summary
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate summary'
      setError(errorMessage)
      handleApiError(error, 'Failed to generate AI summary')
      throw error
    } finally {
      setLoading(false)
    }
  }

  return { generateSummary, loading, error }
}

export function useSecureConversation() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const sendSecureLink = async (conversationId: string) => {
    try {
      setLoading(true)
      setError(null)
      const result = await api.sendSecureConversationLink(conversationId)
      return result
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send secure conversation link'
      setError(errorMessage)
      handleApiError(error, 'Failed to send secure conversation link')
      throw error
    } finally {
      setLoading(false)
    }
  }

  return { sendSecureLink, loading, error }
}